import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellRinging, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEME_PATTERNS = {
  "acres": /\bacres\b/i,
  "tams-3": /\btams\b/i,
  "biss-2026": /\bbiss\b|basic income support/i,
  "scep": /\bscep\b|suckler carbon/i,
  "anc-2026": /\banc\b|natural constraint/i,
  "organic-farming-scheme": /organic farming scheme/i,
  "national-beef-welfare-2026": /beef welfare scheme/i,
  "sheep-welfare-2026": /sheep welfare scheme/i,
};

export const GrantTrackPrompt = ({ article }) => {
  const { user, token } = useAuth();
  const [matches, setMatches] = useState([]);
  const [tracked, setTracked] = useState([]);

  useEffect(() => {
    if (!article) return;
    const text = `${article.title || ""} ${(article.content || "").slice(0, 3000)}`;
    const matchedIds = Object.entries(SCHEME_PATTERNS)
      .filter(([, re]) => re.test(text))
      .map(([id]) => id);
    if (matchedIds.length === 0) return;
    axios.get(`${API}/grants`)
      .then((res) => setMatches(res.data.grants.filter((g) => matchedIds.includes(g.id)).slice(0, 2)))
      .catch(() => {});
  }, [article]);

  useEffect(() => {
    if (!token || matches.length === 0) return;
    axios.get(`${API}/grants/tracked`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setTracked(res.data.grant_ids))
      .catch(() => {});
  }, [token, matches.length]);

  const toggleTrack = async (grant) => {
    if (!token) { toast.error("Sign in (top right) to get deadline reminders"); return; }
    const isTracked = tracked.includes(grant.id);
    try {
      if (isTracked) {
        await axios.delete(`${API}/grants/${grant.id}/track`, { headers: { Authorization: `Bearer ${token}` } });
        setTracked(tracked.filter((id) => id !== grant.id));
        toast.success(`Stopped tracking ${grant.scheme}`);
      } else {
        await axios.post(`${API}/grants/${grant.id}/track`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setTracked([...tracked, grant.id]);
        toast.success(`We'll email you a week before the ${grant.scheme} deadline`);
      }
    } catch { toast.error("Failed to update tracking"); }
  };

  if (matches.length === 0) return null;

  return (
    <div className="mt-8 bg-[#F6FBF9] border-2 border-[#0F766E]" data-testid="grant-track-prompt">
      <div className="px-5 py-3 bg-[#0F766E] flex items-center gap-2">
        <BellRinging size={18} className="text-[#6EE7B7]" weight="fill" />
        <h3 className="font-serif text-lg text-white">This story mentions a farm scheme</h3>
      </div>
      <div className="p-5 space-y-4">
        {matches.map((g) => (
          <div key={g.id} className="flex items-start justify-between gap-4 flex-wrap" data-testid={`grant-prompt-${g.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-[#33302E]">{g.scheme} <span className="font-normal text-[#6B6661]">— {g.full_name}</span></p>
              <p className="text-sm text-[#6B6661] flex items-center gap-1.5 mt-0.5">
                <Clock size={13} className="text-[#B45309]" />
                {g.days_left !== null && g.days_left >= 0 ? `${g.days_left === 0 ? "Deadline TODAY" : `${g.days_left} days to deadline`} — ` : ""}{g.deadline_label}
              </p>
            </div>
            <button onClick={() => toggleTrack(g)} data-testid={`prompt-track-${g.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors flex-shrink-0 ${tracked.includes(g.id) ? "bg-[#0F766E] text-white" : "bg-[#F59E0B] text-[#33302E] hover:bg-[#D97706]"}`}>
              {tracked.includes(g.id) ? <><BellRinging size={14} weight="fill" /> Tracking</> : <><Bell size={14} weight="bold" /> Track this scheme</>}
            </button>
          </div>
        ))}
        <p className="text-xs text-[#6B6661]">
          Tracked schemes email you a reminder a week before the deadline. <Link to="/grants" className="text-[#0F766E] underline" data-testid="prompt-all-grants-link">See all schemes →</Link>
        </p>
      </div>
    </div>
  );
};

export default GrantTrackPrompt;
